import { base64ToBlob } from '@features/SupportChat/utils/base64ToBlob';
import { useCallback, useRef, useState } from 'react';

import { useLazyGetSupportMessageFileQuery } from '@app/api/Support';
import { FRONT_WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { SupportChatMessageFileViewModel } from '@features/SupportChat/types';

export const useSupportChatFileDownload = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { showNotification } = useNotification();

  // Скачивание через скрытую ссылку в окне чата: прямой доступ к DOM
  // (document.createElement и т.п.) запрещён правилами линтера.
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(
    null
  );

  const [getSupportMessageFile] = useLazyGetSupportMessageFileQuery();

  const handleDownloadFile = useCallback(
    async ({ id, name }: SupportChatMessageFileViewModel) => {
      const anchor = downloadAnchorRef.current;

      if (ActiveBranchId == null || !anchor || downloadingFileId !== null) {
        return;
      }

      setDownloadingFileId(id);

      try {
        const file = await getSupportMessageFile({
          ActiveBranchId,
          fileId: id,
        }).unwrap();

        if (!file.Data) {
          showNotification('Файл пустой или больше недоступен', FRONT_WARNING);

          return;
        }

        const url = URL.createObjectURL(base64ToBlob(file.Data));

        anchor.href = url;
        anchor.download = file.Name || name;
        anchor.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setDownloadingFileId(null);
      }
    },
    [ActiveBranchId, downloadingFileId, getSupportMessageFile, showNotification]
  );

  return { downloadingFileId, downloadAnchorRef, handleDownloadFile };
};
